// Multi-file document uploader for clients.
// POSTs multipart/form-data to /api/client/documents/upload-batch (max 5 files, 5MB each).
import { useRef, useState } from "react";
import { UploadCloud, Loader2, CheckCircle2, X, FileText, ExternalLink, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const CATEGORIES = ["ID Verification", "Income Proof", "Bank Statement", "Tax Return", "Business Docs", "Credit Report", "Other"];
const SERVICES = [
  "Credit Profile Development",
  "Capital Access Strategy",
  "Business Foundation & Entity Setup",
  "Authorized User Credit Positioning",
  "Grant Readiness & Funding Research",
  "Personal Credit Development",
  "General",
];

const ACCEPT = "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.zip";
const MAX_MB = 5;
const MAX_FILES = 5;

// Mirror of backend ALLOWED_MIME_PREFIXES — keeps client + server in sync.
// Browser `accept` attribute is only a hint; we re-validate here so a renamed
// file or drag-and-drop bypass still gets caught client-side before submit.
const ALLOWED_MIME_PREFIXES = [
  "image/",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument",
  "text/plain",
  "application/zip",
];
// Fallback by extension when the browser reports an empty `type` (some
// .doc/.xls files do this on Safari).
const ALLOWED_EXTS = new Set([
  "pdf", "doc", "docx", "xls", "xlsx", "txt", "zip",
  "png", "jpg", "jpeg", "gif", "webp", "heic", "heif", "bmp", "svg",
]);

function validateMime(file) {
  const type = (file.type || "").toLowerCase();
  if (type && ALLOWED_MIME_PREFIXES.some((p) => type.startsWith(p))) return null;
  const ext = (file.name || "").split(".").pop().toLowerCase();
  if (ALLOWED_EXTS.has(ext)) return null;
  return `Unsupported type: ${type || ext || "unknown"}`;
}

function fmtKb(n) {
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024).toFixed(0)} KB`;
}

export default function DocumentUploader({ existing = [], onUploaded }) {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]); // [{file, error?}]
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [service, setService] = useState(SERVICES[6]);
  const [state, setState] = useState({ status: "idle", error: "", results: null });

  const reset = () => {
    setFiles([]);
    setCategory(CATEGORIES[0]);
    setService(SERVICES[6]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    const merged = [...files];
    for (const f of incoming) {
      if (merged.length >= MAX_FILES) break;
      // Two-layer validation: size first, then MIME (so a 6MB mp4 says "Exceeds 5 MB" not "Unsupported type").
      let err = null;
      if (f.size > MAX_MB * 1024 * 1024) err = `Exceeds ${MAX_MB} MB`;
      else err = validateMime(f);
      merged.push({ file: f, error: err });
    }
    setFiles(merged);
    setState({ status: "idle", error: "", results: null });
  };

  const removeAt = (i) => setFiles(files.filter((_, j) => j !== i));

  const validFiles = files.filter((f) => !f.error);
  const hasErrors = files.some((f) => f.error);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (validFiles.length === 0) {
      setState({ status: "error", error: "Add at least one valid file.", results: null });
      return;
    }
    setState({ status: "loading", error: "", results: null });

    const fd = new FormData();
    validFiles.forEach((f) => fd.append("files", f.file));
    fd.append("document_category", category);
    fd.append("service_related_to", service);

    try {
      const token = localStorage.getItem("jc3_token");
      const res = await fetch(`${API}/api/client/documents/upload-batch`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.detail || `Upload failed (${res.status})`);

      const { summary, status, results } = body || {};
      if (status === "ok") {
        setState({ status: "success", error: "", results });
        reset();
      } else if (status === "partial") {
        setState({ status: "partial", error: `${summary?.ok || 0} uploaded, ${summary?.failed || 0} failed, ${summary?.partial || 0} attachment-only failures.`, results });
        setFiles(files.filter((f) => results?.some((r) => r.filename === f.file.name && r.status !== "ok")));
      } else {
        setState({ status: "error", error: "All uploads failed. See per-file errors below.", results });
      }
      onUploaded && onUploaded(body);
      setTimeout(() => setState((s) => (s.status === "success" ? { status: "idle", error: "", results: null } : s)), 8000);
    } catch (err) {
      setState({ status: "error", error: err.message || "Upload failed.", results: null });
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-jc-gold/30 px-0 py-3 font-body text-base text-jc-white placeholder-jc-muted focus:border-jc-gold focus:outline-none transition-colors";
  const labelCls = "font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold";

  return (
    <div data-testid="document-uploader" className="border border-jc-gold/25 bg-jc-black/40 backdrop-blur-sm p-6 md:p-8">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold mb-2">◆ Documents</p>
          <h3 className="font-display text-2xl md:text-3xl text-jc-white tracking-wide">UPLOAD DOCUMENTS</h3>
        </div>
        <FileText size={32} className="text-jc-gold/40" />
      </div>
      <p className="font-body text-base text-jc-muted mb-6">Up to {MAX_FILES} files per submission. {MAX_MB} MB max each.</p>

      <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
        <div className="sm:col-span-2">
          <label className={labelCls}>Files</label>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 px-4 py-3 border border-jc-gold/40 text-jc-gold hover:bg-jc-gold hover:text-jc-black transition-colors cursor-pointer font-mono text-[11px] uppercase tracking-[0.25em]">
              <UploadCloud size={14} /> Choose Files
              <input
                ref={inputRef}
                data-testid="upload-file-input"
                type="file"
                multiple
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
              />
            </label>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-jc-muted">{validFiles.length} ready · {files.length}/{MAX_FILES} added</span>
          </div>

          {files.length > 0 && (
            <ul className="mt-4 space-y-2" data-testid="upload-queue">
              {files.map((f, i) => (
                <li key={i} className={`flex items-center justify-between gap-3 px-3 py-2 border ${f.error ? "border-red-500/40 bg-red-500/5" : "border-jc-gold/20 bg-jc-black/30"}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {f.error ? <AlertTriangle size={12} className="text-red-300 shrink-0" /> : <CheckCircle2 size={12} className="text-emerald-300 shrink-0" />}
                    <span className="font-mono text-xs text-jc-white truncate">{f.file.name}</span>
                    <span className="font-mono text-[10px] text-jc-muted">({fmtKb(f.file.size)})</span>
                    {f.error && <span className="font-mono text-[10px] text-red-300">— {f.error}</span>}
                  </div>
                  <button type="button" onClick={() => removeAt(i)} className="text-jc-muted hover:text-red-300" data-testid={`upload-remove-${i}`} aria-label="Remove file"><X size={12} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <label className={labelCls}>Category</label>
          <select data-testid="upload-category" value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} appearance-none cursor-pointer`}>
            {CATEGORIES.map((c) => <option key={c} value={c} className="bg-jc-navy text-jc-white">{c}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Service It Relates To</label>
          <select data-testid="upload-service" value={service} onChange={(e) => setService(e.target.value)} className={`${inputCls} appearance-none cursor-pointer`}>
            {SERVICES.map((s) => <option key={s} value={s} className="bg-jc-navy text-jc-white">{s}</option>)}
          </select>
        </div>

        <div className="sm:col-span-2 mt-3 flex flex-wrap items-center gap-4">
          <button
            type="submit"
            disabled={state.status === "loading" || validFiles.length === 0}
            data-testid="upload-submit"
            className="jc-btn jc-btn-primary !py-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {state.status === "loading" ? (
              <><Loader2 size={14} className="animate-spin" /> UPLOADING {validFiles.length}...</>
            ) : (
              <><UploadCloud size={14} /> UPLOAD {validFiles.length > 1 ? `${validFiles.length} FILES` : "FILE"}</>
            )}
          </button>
          {state.status === "success" && (
            <span data-testid="upload-success" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-emerald-300">
              <CheckCircle2 size={14} /> Uploaded. Jace will review shortly.
            </span>
          )}
          {state.status === "partial" && (
            <span data-testid="upload-partial" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-amber-300">
              <AlertTriangle size={14} /> {state.error}
            </span>
          )}
          {state.status === "error" && (
            <span data-testid="upload-error" className="font-mono text-xs uppercase tracking-[0.22em] text-red-400">{state.error}</span>
          )}
        </div>

        {/* per-file results from last batch */}
        {state.results && state.results.length > 0 && (
          <div className="sm:col-span-2 border-t border-jc-gold/15 pt-4 mt-2">
            <p className={labelCls}>Last Batch Results</p>
            <ul className="mt-3 space-y-1">
              {state.results.map((r, i) => (
                <li key={i} className="flex items-center justify-between gap-3 font-mono text-[11px] uppercase tracking-[0.18em]">
                  <span className="text-jc-white truncate">{r.filename}</span>
                  <span className={r.status === "ok" ? "text-emerald-300" : r.status === "partial" ? "text-amber-300" : "text-red-300"}>
                    {r.status === "ok"
                      ? "✓ Uploaded"
                      : r.status === "partial"
                        ? "⚠ Record only"
                        : `✗ ${r.message || r.error || r.error_code || "failed"}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {hasErrors && state.status === "idle" && (
          <p className="sm:col-span-2 font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">⚠ Some files have issues — fix or remove them before submitting.</p>
        )}
      </form>

      {/* Existing files list */}
      {existing && existing.length > 0 && (
        <div className="mt-8 border-t border-jc-gold/15 pt-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold mb-3">◆ Your Files ({existing.length})</p>
          <ul className="space-y-2">
            {existing.map((d) => {
              const att = Array.isArray(d["File Attachment"]) ? d["File Attachment"][0] : null;
              return (
                <li key={d.id} className="flex items-center justify-between gap-4 py-2 border-b border-jc-gold/10">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText size={14} className="text-jc-gold shrink-0" />
                    <div className="min-w-0">
                      <div className="font-body text-jc-white truncate">{d["Document Name"] || d.Name || "Untitled"}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-jc-muted truncate">
                        {d["Document Category"] || "—"} · {d["Service Related To"] || "—"} · {d["Review Status"] || "Pending Review"}
                      </div>
                    </div>
                  </div>
                  {att?.url && (
                    <a href={att.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-jc-gold hover:text-jc-gold-light">
                      View <ExternalLink size={11} />
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
